import React, { useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { AccountSelect, money, sortAccounts } from './shared';

const KIND_LABEL = { payment: 'Payment', invoice: 'Open invoice', expense: 'Expense', bill: 'Bill', account: 'Rule' };

/** Say what a bank-feed line is: a recorded payment or expense, an open invoice
 *  or bill, or a new expense / income categorized to an account. */
export default function MatchDialog({ txn, accounts, onClose, onDone }) {
  const [suggestions, setSuggestions] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  const deposit = Number(txn?.amount) > 0;

  useEffect(() => {
    setSuggestions(null);
    setAccountId('');
    if (txn && !txn.matched) ledgerApi.suggestions(txn.id).then((d) => setSuggestions(d.suggestions)).catch(() => setSuggestions([]));
  }, [txn]);

  const match = async (kind, targetId) => {
    setBusy(true);
    try {
      await ledgerApi.match(txn.id, kind, targetId);
      toast.success('Bank line matched');
      onDone();
    } catch (e) {
      toast.error(apiError(e, 'Could not match'));
    } finally {
      setBusy(false);
    }
  };

  const unmatch = async () => {
    setBusy(true);
    try { await ledgerApi.unmatch(txn.id); toast.success('Match removed'); onDone(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const categoryFilter = deposit
    ? (a) => ['Income', 'Other Income', 'Equity', 'Long Term Liability', 'Other Current Liability'].includes(a.type)
    : (a) => ['Expense', 'Cost of Goods Sold', 'Other Expense', 'Fixed Asset', 'Equity', 'Long Term Liability', 'Other Current Liability'].includes(a.type);

  return (
    <Dialog open={Boolean(txn)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{txn?.matched ? 'Matched bank line' : 'Match bank line'}</DialogTitle></DialogHeader>
        {txn ? (
          <div className="rounded-md border border-slate-200 p-3 text-sm flex justify-between gap-3">
            <div><div className="font-semibold text-slate-900">{txn.desc || txn.description}</div><div className="text-xs text-slate-500">{txn.date} · {txn.account}</div></div>
            <div className={`font-bold tabular-nums ${deposit ? 'text-emerald-600' : 'text-red-600'}`}>{deposit ? '+' : ''}{money(txn.amount)}</div>
          </div>
        ) : null}
        {txn?.matched ? (
          <>
            <p className="text-sm text-slate-600">Matched to {txn.matchedKind ? `${KIND_LABEL[txn.matchedKind] || txn.matchedKind} ${txn.matchedId}` : 'a record (before the books existed)'}.
              Unmatching leaves that record in place; delete it from its own screen if it was wrong.</p>
            <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button><Button variant="outline" className="gap-1.5" onClick={unmatch} disabled={busy}><Unlink className="w-4 h-4" />Unmatch</Button></DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Suggested matches</Label>
              {suggestions === null ? <div className="text-sm text-slate-500">Looking…</div> : !suggestions.length ? (
                <div className="text-sm text-slate-500">No recorded {deposit ? 'payment or open invoice' : 'expense or bill'} has this amount. Categorize it below.</div>
              ) : suggestions.map((s) => (
                <div key={`${s.kind}-${s.targetId}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate"><Badge className="border-0 bg-slate-100 text-slate-600 mr-2">{KIND_LABEL[s.kind] || s.kind}</Badge>{s.label}</div>
                    <div className="text-xs text-slate-500">{s.reason} · {Math.round(s.confidence * 100)}% sure</div>
                  </div>
                  <Button size="sm" className="h-7 gap-1 bg-[#8BC53F] hover:bg-[#6FA82C]" disabled={busy} onClick={() => match(s.kind, s.targetId)}><Link2 className="w-3.5 h-3.5" />Match</Button>
                </div>
              ))}
            </div>
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <Label className="text-xs text-slate-500">{deposit ? 'Or record it as income to' : 'Or record it as a new expense to'}</Label>
              <div className="flex gap-2">
                <AccountSelect accounts={sortAccounts(accounts)} filter={categoryFilter} value={accountId} onChange={setAccountId} className="h-9 flex-1" testId="match-account" />
                <Button className="h-9" variant="outline" disabled={busy || !accountId} onClick={() => match('account', accountId)}>Categorize</Button>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
